import { StepType } from "@redotech/redo-model/advanced-flow/common";
import { ContactChannel } from "@redotech/redo-model/advanced-flow/rules-v3/schemas";
import { TimeUnit } from "@redotech/redo-model/advanced-flow/rules-v3/types";
import {
  ErrorHandlingStrategy,
  QueryOperator,
  RuleActionType,
  RuleConditionType,
  RuleTriggerType,
} from "@redotech/redo-model/advanced-flow/rules/types";
import { SchemaType } from "@redotech/redo-model/advanced-flow/schemas/schemas";
import { Schema } from "effect";
import { makeBind } from "../field-binding";
import { DefaultRule } from "../types";

/**
 * Shape of this rule's `configurations` JSONB blob. The merchant fills in
 * a list of exact email addresses or domains; any conversation whose sender
 * matches one of them is skipped.
 */
export const AutoMergeOnConversationCreatedConfigSchema = Schema.Struct({
  autoMergeExclusions: Schema.Array(Schema.String),
});

export type AutoMergeOnConversationCreatedConfig =
  typeof AutoMergeOnConversationCreatedConfigSchema.Type;

const bind = makeBind<AutoMergeOnConversationCreatedConfig>();

/**
 * Default rule: Auto-merge on conversation created
 *
 * When a new conversation is created, find and merge the customer's most
 * recent open conversation (within the last 24 hours) into the new one.
 * Runs for all channels. Senders matching the exclusion list are skipped;
 * for non-email channels without a sender address the exclusion check
 * vacuously passes.
 *
 * Enabled when `autoMergeEnabled` is true in the team's support settings.
 * Starts disabled — merchants opt in via the General settings toggle.
 */
export const autoMergeOnConversationCreatedRule = {
  id: "default-auto-merge-on-conversation-created",
  name: "Auto-merge open tickets",
  description:
    "When a customer starts a new conversation, automatically merge it into their existing open ticket from the last 24 hours.",
  hidden: false,
  triggerType: RuleTriggerType.CONVERSATION_CREATED,

  configSchema: AutoMergeOnConversationCreatedConfigSchema,

  defaultConfig: {
    defaultRuleId: "default-auto-merge-on-conversation-created",
    enabled: false,
    sortIndex: 3,
    configurations: { autoMergeExclusions: [] },
  },

  template: {
    name: "Auto-merge open tickets",
    description:
      "When a customer starts a new conversation, automatically merge it into their existing open ticket from the last 24 hours.",
    enabled: true,
    schemaType: SchemaType.RULES_V3,
    steps: [
      {
        type: StepType.TRIGGER,
        id: "trigger",
        schemaType: SchemaType.RULES_V3,
        category: "Rules",
        key: RuleTriggerType.CONVERSATION_CREATED,
        nextId: "conditions",
      },
      {
        type: StepType.RULE_CONDITIONS_V3,
        id: "conditions",
        conditionGroups: {
          operator: "AND",
          conditions: [
            {
              conditionType: RuleConditionType.SENT_FROM,
              channel: ContactChannel.EMAIL,
              // Excluded senders do not get auto-merged. Empty list passes
              // vacuously (no one is excluded). Non-email channels also
              // pass because they have no sender address to match.
              values: bind("autoMergeExclusions", []),
              queryOperator: QueryOperator.DOES_NOT_CONTAIN,
            },
          ],
        },
        nextTrueId: "actions",
      },
      {
        type: StepType.RULE_ACTIONS_V3,
        id: "actions",
        actions: [
          {
            actionType: RuleActionType.MERGE_SUPPORT_TICKET,
            allowedStatuses: ["OPEN"],
            maxAgeSinceLastMessage: { value: 24, unit: TimeUnit.HOURS },
          },
        ],
        errorHandlingStrategy: ErrorHandlingStrategy.CONTINUE_ON_ERROR,
      },
    ],
  },
} as const satisfies DefaultRule<string, AutoMergeOnConversationCreatedConfig>;
